import React, { useState, useRef, useEffect } from 'react'
import {
    Image,
    Text,
    Dimensions,
    View,
    Animated,
    ImageBackground,
    Easing,
    TouchableOpacity,
    Platform,
    StyleSheet,
    ScrollView,
    Pressable,
} from 'react-native'
import { GestureDetector, Gesture } from 'react-native-gesture-handler'
import { isWeb } from '../../../utils/platformUtils'
import themes from '../../../../theme'
import VideoPlayerAV from '../VideoPlayer/expo-av'
import { WebViewButton } from './WebViewScreen'
import { WebRedirect } from './WebRedirect'
import PostSurvey from '../SurveyBlock/PostSurvey'
import { update_complete_local_posts } from '../../../db/posts/index'
import { ImageWithZoom } from './ImageWIthZoom'
import AudioPlayerWaveform from './AudioPlayerWaveform'
import styles from './MediaComponentStyles'
import SizeCache from '../../../utils/sizeCache'
// Usar ScrollView nativo para máxima compatibilidad en web y móvil
import { Ionicons, Feather } from '@expo/vector-icons'
import LearningFullscreen from './LearningFullscreen'
import * as NavigationBar from 'expo-navigation-bar'

const defaultWidth = Dimensions.get('window').width

const MediaComponent = ({
    medias,
    onPageChange,
    onEndReached,
    handleCompleteSurvey,
    isComplete,
    post_id,
    autoplay,
    isVisible,
    contentSeen,
    path,
    isGoTo,
    isPost,
    isPostPlayer = false,
    // Nuevos props (opcionales) para learnings
    autoAdvanceOnVideoEnd = false,
    countdownSeconds = 0,
    resumeKey,
    // Reacciones: long press y double tap (mobile)
    onLongPressReaction,
    onDoubleTapReaction,
    // Último módulo del curso -> completar inmediatamente al ver el último slide
    isUltimateModule = false,
    onLastContentSeen,
    // Nuevo: avisar al padre (PostsList) cuando el primer media del post está listo
    onFirstMediaReady,
    // Índice del post en la lista para gatear overlay con el top item
    listIndex,
    // Callback cuando una imagen está en zoom
    onImageZoomChange,
    doubleClickEvent,
}) => {
    const DEBUG_GESTURES = false
    const [currentIndex, setCurrentIndex] = useState(0)
    const [animation] = useState(new Animated.Value(0))
    const [isImageZooming, setIsImageZooming] = useState(false) // Estado local para elevar zIndex
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0)
    const scrollX = useRef(new Animated.Value(0)).current
    const videoRefs = useRef([])
    const audioRefs = useRef([])
    const flatListRef = useRef(null)
    const innerContainerRef = useRef(null) // Referencia al contenedor interno de RN Web que se mueve con transform
    const viewabilityConfig = useRef({ viewAreaCoveragePercentThreshold: 50 })
    const isManualNavigation = useRef(false) // Flag para detectar navegación manual
    const isUserDraggingRef = useRef(false) // Flag para detectar swipe manual
    // En mobile iniciar con el ancho de pantalla; en web medir el ancho real del contenedor (CSS Columns)
    const [containerWidth, setContainerWidth] = useState(Platform.OS === 'web' ? 0 : defaultWidth)
    const rootRef = useRef(null)
    const [readyMap, setReadyMap] = useState({})
    const [heightMap, setHeightMap] = useState({}) // altura fija por índice cuando conocemos ratio
    const naturalSizeCapturedRef = useRef({}) // evita procesar onNaturalSize más de una vez por índice
    const lastMeasuredWidthRef = useRef(0) // Evitar updates innecesarios de containerWidth
    // Countdown para learnings
    const isLearning = path === 'learning'
    const isPosts = path === 'post'
    const [countdownRemaining, setCountdownRemaining] = useState(null)
    // Interval ref for the state-driven countdown
    const countdownIntervalRef = useRef(null)
    // Ref to avoid handling the same playback-cancel event repeatedly per index
    const countdownCancelledRef = useRef({})
    const [countdownActive, setCountdownActive] = useState(false)
    // Permitir que el player se auto-reproduzca al finalizar el countdown (aunque autoplay=false)
    const [shouldPlayAfterCountdown, setShouldPlayAfterCountdown] = useState(false)
    const [isNavigatingMedia, setIsNavigatingMedia] = useState(false)

    // DEBUG: Log cuando countdownRemaining cambia
    useEffect(() => {
        console.log('[MediaComponent][DEBUG] countdownRemaining changed to:', countdownRemaining, 'countdownActive:', countdownActive)
    }, [countdownRemaining, countdownActive])

    // Evitar múltiples avances por fin de video
    const endHandledRef = useRef({})
    // Estado de fullscreen por índice (nativo)
    const isFullscreenRef = useRef({})
    // Re-entrada a fullscreen en el siguiente slide (nativo)
    const pendingReenterFsIndex = useRef(null)
    // Fullscreen propio para learning (web y nativo)
    const [showFs, setShowFs] = useState(false)
    const lastInlineStatusRef = useRef({ position: 0, isPlaying: false })
    const [inlinePlayback, setInlinePlayback] = useState({}) // { [index]: { position, duration, isPlaying } }

    const inlineTrackWidthRef = useRef({}) // { [index]: number }
    const inlineTrackRef = useRef({}) // { [index]: element }
    // Mostrar overlay de "Volver a ver" cuando un video termina (modo posts)
    const [endedMap, setEndedMap] = useState({}) // { [index]: true }

    // Resetear skeleton states sólo si cambia realmente el set de medias (firma por url+tipo)
    const lastMediasSigRef = useRef('')
    useEffect(() => {
        try {
            const sig = Array.isArray(medias)
                ? medias
                      .map((m) => `${m?.media_url || m?.media || ''}:${(m?.media_type || m?.content_type || '').toString().toLowerCase()}`)
                      .join('|')
                : ''
            if (sig !== lastMediasSigRef.current) {
                lastMediasSigRef.current = sig
                setReadyMap({})
                naturalSizeCapturedRef.current = {}
            }
        } catch {}
    }, [medias])
    useEffect(() => {
        if (doubleClickEvent && flatListRef.current) {
            flatListRef.current.addEventListener('dblclick', doubleClickEvent, true)
            return () => {
                flatListRef.current.removeEventListener('dblclick', doubleClickEvent, true)
            }
        }
    }, [])

    // Listener único de scrollX para actualizar currentIndex
    useEffect(() => {
        const listener = scrollX.addListener(({ value }) => {
            const width = containerWidth || defaultWidth
            const index = Math.round(width > 0 ? value / width : 0)
            setCurrentIndex(index)
            if (!isManualNavigation.current) {
                onPageChange(index === medias.length - 1)
            }
        })
        // No disparar onEndReached desde el componente de media; lo maneja la lista padre
        return () => {
            if (listener) scrollX.removeListener(listener)
        }
    }, [scrollX, medias.length, onPageChange, containerWidth])

    const lastViewableLogRef = useRef(0)
    const onViewableItemsChanged = useRef(({ viewableItems, changed }) => {
        // console.log('🚀 ~ onViewableItemsChanged ~ viewableItems, changed:', viewableItems, changed)
        changed.forEach(({ item, index, isViewable }) => {
            if (item.media_type === 'video') {
                if (isViewable) {
                    //   videoRefs.current[index]?.playAsync();
                } else {
                    //   videoRefs.current[index]?.pauseAsync();
                }
            }
        })
        // Throttle opcional del log (desactivado para producción)
        // const now = Date.now();
        // if (now - lastViewableLogRef.current > 1000) {
        //     lastViewableLogRef.current = now;
        //     try {
        //         const first = viewableItems && viewableItems[0]
        //         if (first) {
        //             console.log('[MediaComponent] onViewableItemsChanged -> first index:', first.index, 'type:', first.item?.media_type || first.item?.content_type)
        //         }
        //     } catch {}
        // }
    }).current

    const getItemLayout = React.useCallback(
        (data, index) => ({
            length: containerWidth,
            offset: containerWidth * index,
            index,
        }),
        [containerWidth],
    )

    const keyExtractor = React.useCallback((item, index) => {
        // Usar una key estable basada en el contenido del item
        const url = item?.media_url || item?.media || ''
        return `${index}-${url.slice(-20)}`
    }, [])

    const onScroll = Animated.event([{ nativeEvent: { contentOffset: { x: scrollX } } }], { useNativeDriver: false })

    // (eliminado segundo listener duplicado)

    useEffect(() => {
        // Al cambiar el índice de video, pausar otros sin descargarlos para evitar flickers
        videoRefs?.current.forEach((ref, index) => {
            if (index !== currentVideoIndex) {
                ref?.pauseAsync()
            }
        })
        audioRefs?.current.forEach((ref, index) => {
            if (index !== currentVideoIndex) {
                ref?.pauseAsync()
            }
        })
    }, [currentVideoIndex])

    useEffect(() => {
        // Solo resetear a 0 si no estamos en medio de navegación manual
        if (!isManualNavigation.current) {
            setCurrentIndex(0)
            onPageChange(false)
            // En mobile + autoplay, si el primer media es video, intentar reproducirlo
            if (Platform.OS !== 'web' && autoplay) {
                setTimeout(() => {
                    try {
                        const firstType = (medias?.[0]?.media_type || medias?.[0]?.content_type || '').toString().toLowerCase()
                        if (firstType === 'video' || firstType === 'podcast') {
                            playVideo(0)
                        }
                    } catch {}
                }, 180)
            }
            // Learning: autoplay inicial con 2s de delay, sin countdown, sólo si el primer media es video y no hay countdown activo
            try {
                const firstType = (medias?.[0]?.media_type || medias?.[0]?.content_type || '').toString().toLowerCase()
                if (isLearning && (firstType === 'video' || firstType === 'podcast') && (!countdownSeconds || countdownSeconds <= 0)) {
                    setTimeout(() => {
                        try {
                            playVideo(0)
                        } catch {}
                    }, 2000)
                }
            } catch {}
        }
    }, [medias, autoplay])

    useEffect(() => {
        //  pauseVideo(currentVideoIndex);
        setCurrentVideoIndex(currentIndex)
        // playVideo(currentIndex);
        // Reset de bandera near-end para el índice actual
        try {
            endHandledRef.current[currentIndex] = false
        } catch {}
        // Si hay una re-entrada de fullscreen pendiente y estamos en ese índice, intentar re-entrar
        if (Platform.OS !== 'web') {
            try {
                if (pendingReenterFsIndex.current === currentIndex && videoRefs.current[currentIndex]?.presentFullscreenPlayer) {
                    // Pequeño delay para asegurar que el ref esté listo
                    setTimeout(() => {
                        try {
                            videoRefs.current[currentIndex]?.presentFullscreenPlayer?.()
                        } catch {}
                        pendingReenterFsIndex.current = null
                    }, 150)
                }
            } catch {}
        }
    }, [currentIndex])

    // Inicializar/limpiar countdown al cambiar de slide (estado-driven)
    useEffect(() => {
        // Limpiar interval existente al cambiar de índice
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current)
            countdownIntervalRef.current = null
        }
        setCountdownActive(false)
        setCountdownRemaining(null)
        setShouldPlayAfterCountdown(false)

        // Si estamos en learnings y el slide actual es un video y hay countdown configurado
        try {
            const cur = medias?.[currentIndex]
            const t = (cur?.media_type || cur?.content_type || '').toString().toLowerCase()
            const isVideo = t === 'video'
            const manual = isManualNavigation.current || isUserDraggingRef.current
            if (isLearning && isVideo && countdownSeconds > 0) {
                // Si la navegación fue manual (flechas o swipe), reproducir inmediatamente y NO mostrar countdown
                if (manual) {
                    try {
                        playVideo(currentIndex)
                    } catch {}
                    setCountdownActive(false)
                    setCountdownRemaining(0)
                    return
                }

                // Inicio del countdown (estado-driven)
                try {
                    const inst = `${post_id || path || 'learning'}:${currentIndex}`
                    console.log('[MediaComponent][countdown] start', { inst, currentIndex, countdownSeconds, manual })
                } catch (e) {}

                // capture the index for the interval closure
                const localIndex = currentIndex

                setCountdownActive(true)
                countdownCancelledRef.current[localIndex] = false
                setCountdownRemaining(countdownSeconds)

                // Pausar el player mientras corre la cuenta
                try {
                    videoRefs.current[localIndex]?.pauseAsync()
                } catch (e) {}

                // Usar setInterval que decrementa el estado; no mutamos props
                const startTime = Date.now()

                countdownIntervalRef.current = setInterval(() => {
                    setCountdownRemaining((prev) => {
                        const current = typeof prev === 'number' ? prev : countdownSeconds
                        const next = current - 1
                        try {
                            console.log('[MediaComponent][countdown] tick', { inst: `${post_id || path || 'learning'}:${localIndex}`, value: next })
                        } catch (e) {}
                        if (next <= 0) {
                            // final
                            if (countdownIntervalRef.current) {
                                clearInterval(countdownIntervalRef.current)
                                countdownIntervalRef.current = null
                            }
                            setCountdownActive(false)
                            setShouldPlayAfterCountdown(true)
                            // Play video after state updates and unmute
                            setTimeout(() => {
                                try {
                                    // Unmute before playing
                                    videoRefs.current[localIndex]?.setIsMutedAsync?.(false)
                                    playVideo(localIndex)
                                } catch (e) {}
                            }, 50)
                            return 0
                        }
                        return next
                    })
                }, 1000)
            }
        } catch {}

        return () => {
            if (countdownIntervalRef.current) {
                try {
                    console.log('[MediaComponent][countdown] cleanup', { inst: `${post_id || path || 'learning'}:${currentIndex}` })
                } catch (e) {}
                clearInterval(countdownIntervalRef.current)
                countdownIntervalRef.current = null
                countdownCancelledRef.current[currentIndex] = false
            }
        }
    }, [currentIndex, medias, isLearning, countdownSeconds])

    useEffect(() => {
        // Pausar el video saliente; en autoplay, sólo reproducir si no hay countdown activo
        pauseVideo(currentVideoIndex)
        const shouldDelayAutoplay = isLearning && countdownSeconds > 0 && countdownActive
        const allowAuto = Platform.OS !== 'web' && autoplay && !shouldDelayAutoplay
        if (allowAuto) {
            playVideo(currentIndex)
        }
    }, [currentVideoIndex, countdownActive, autoplay])

    // Si el post deja de ser visible (por ejemplo al scrollear varios posteos rápido),
    // asegurarnos de pausar todos los videos locales para evitar reproducción residual.
    useEffect(() => {
        try {
            if (!isVisible) {
                // Pausar todos los refs registrados en este componente
                videoRefs.current?.forEach((ref) => {
                    try {
                        ref?.pauseAsync?.()
                    } catch {}
                })
                audioRefs.current?.forEach((ref) => {
                    try {
                        ref?.pauseAsync?.()
                    } catch {}
                })
            }
        } catch {}
    }, [isVisible])

    const playVideo = (index) => {
        // En native (Android/iOS) sólo reproducir si el post y el slide están realmente visibles.
        // Evita que un playAsync se dispare por efecto colateral cuando el item ya salió de pantalla.
        try {
            if (Platform.OS !== 'web') {
                const shouldBeVisible = !!isVisible && currentIndex === index
                if (!shouldBeVisible) return
            }
        } catch {}
        if (videoRefs.current[index]) {
            try {
                // Unmute if this is after countdown (learning mode)
                if (isLearning && shouldPlayAfterCountdown) {
                    videoRefs.current[index].setIsMutedAsync?.(false)
                }
                videoRefs.current[index].playAsync()
            } catch {}
        }
    }

    const pauseVideo = (index) => {
        if (videoRefs.current[index]) {
            try {
                videoRefs.current[index].pauseAsync()
            } catch {}
        }
    }

    let navigationTimeout = null

    const goToNext = (manual = true) => {
        if (currentIndex < medias.length - 1 && flatListRef.current) {
            const newIndex = currentIndex + 1
            performNavigation(newIndex, manual)
        }
    }

    const goToPrevious = (manual = true) => {
        if (currentIndex > 0 && flatListRef.current) {
            const newIndex = currentIndex - 1
            performNavigation(newIndex, manual)
        }
    }
    const performNavigation = (newIndex, manual) => {
        if (isNavigatingMedia) return

        if (manual) setIsNavigatingMedia(true)

        const width = containerWidth || defaultWidth
        const offset = newIndex * width

        if (manual) isManualNavigation.current = true

        // 1. Actualización de estado inmediata (CRÍTICO para evitar el doble clic)
        setCurrentIndex(newIndex)
        scrollX.setValue(offset)

        if (Platform.OS === 'web') {
            try {
                // 2. Intentar usar la referencia guardada
                let elementToMove = innerContainerRef.current

                // 3. Si no existe, buscarla una sola vez de forma eficiente
                if (!elementToMove && flatListRef.current) {
                    const scrollNode = flatListRef.current.getScrollableNode?.()
                    if (scrollNode) {
                        // En React Native Web, el contenedor real suele ser el primer hijo del nodo de scroll
                        elementToMove = scrollNode.querySelector('[data-testid="flatlist-container"]') || scrollNode.children[0]
                        innerContainerRef.current = elementToMove
                    }
                }

                // 4. Aplicar el movimiento
                if (elementToMove) {
                    elementToMove.style.transition = 'transform 0.3s ease-out' // Asegura suavidad
                    elementToMove.style.transform = `translateX(-${offset}px)`
                }
            } catch (err) {
                console.error('[MediaComponent] Error moviendo el DOM:', err)
                setIsNavigatingMedia(false)
            }
        } else {
            flatListRef.current.scrollToIndex({ index: newIndex, animated: true })
        }

        // 5. Gestión de banderas y callbacks
        if (manual) {
            if (navigationTimeout) clearTimeout(navigationTimeout)
            navigationTimeout = setTimeout(() => {
                isManualNavigation.current = false
                onPageChange(newIndex === medias.length - 1)
                setIsNavigatingMedia(false)
            }, 350)
        }
    }
    const doublePress = () => {
        console.log('Llamé al doublePress', isNavigatingMedia)
        if (isNavigatingMedia) return
        onDoubleTapReaction?.()
    }
    const renderItem = React.useCallback(
        ({ item, index }) => {
            //TODO: modificar desde base para que todo sea como el muro

            let type
            let url
            let name = item.media_name ?? ''
            if (item.media_type) {
                //Muro
                type = item?.media_type?.toString().toLowerCase()
                url = item.media_url
            } else {
                //Contenido
                type = item?.content_type?.toString().toLowerCase()
                url = item.media
            }

            const webViewUrl = url

            try {
                // debug suprimido: renderItem
            } catch {}

            // Ratio inicial derivado de metadata del servicio si existe (incluye campos alternativos comunes)
            const ratioFromMeta = (() => {
                try {
                    // 1) Campos directos
                    const direct = Number(item?.aspect_ratio || item?.ratio)
                    if (direct > 0.2 && direct < 5) return direct
                    // 2) width/height estándar en media
                    const w = Number(item?.width)
                    const h = Number(item?.height)
                    if (w > 0 && h > 0) return w / h
                    // 3) Campos de imagen/poster/thumbnail reportados por backend
                    const iw = Number(item?.image_width || item?.poster_width || item?.thumbnail_width || item?.thumb_width)
                    const ih = Number(item?.image_height || item?.poster_height || item?.thumbnail_height || item?.thumb_height)
                    if (iw > 0 && ih > 0) return iw / ih
                    // 4) Matrices anidadas poco comunes
                    const metaW = Number(item?.meta?.width)
                    const metaH = Number(item?.meta?.height)
                    if (metaW > 0 && metaH > 0) return metaW / metaH
                    // 5) Fallback: ratio persistido por URL (si ya lo tenemos cacheado del precálculo)
                    const persisted = url ? SizeCache.getRatioForUrl(url) : null
                    if (typeof persisted === 'number' && persisted > 0.2 && persisted < 5) return persisted
                    // 6) Parseo desde la URL (patrones 1080x1920, w/h en query, 1080-1920)
                    if (typeof url === 'string') {
                        // 1080x1920
                        let m = url.match(/(\d{3,5})[xX](\d{3,5})/)
                        if (m && m[1] && m[2]) {
                            const rw = Number(m[1]),
                                rh = Number(m[2])
                            if (rw > 0 && rh > 0) return rw / rh
                        }
                        // ?w=1080&h=1920 ó ?width&height
                        const q = url.split('?')[1]
                        if (q) {
                            const params = new URLSearchParams(q)
                            const rw = Number(params.get('w') || params.get('width'))
                            const rh = Number(params.get('h') || params.get('height'))
                            if (rw > 0 && rh > 0) return rw / rh
                        }
                        // 1080-1920
                        m = url.match(/(\d{3,5})-(\d{3,5})(?!\d)/)
                        if (m && m[1] && m[2]) {
                            const rw = Number(m[1]),
                                rh = Number(m[2])
                            if (rw > 0 && rh > 0) return rw / rh
                        }
                    }
                } catch {}
                return null
            })()
            const webItemRadius = { borderRadius: 0, overflow: 'hidden' }
            switch (type) {
                case 'image':
                    return (
                        <View style={[{ width: containerWidth, overflow: 'visible' }, Platform.OS == 'web' && webItemRadius]}>
                            <ImageWithZoom
                                uri={url}
                                contentSeen={setContentSeen}
                                path={path}
                                containerWidth={containerWidth}
                                initialRatio={ratioFromMeta}
                                onReady={() => {
                                    setReadyMap((m) => ({ ...m, [index]: true }))
                                    try {
                                        if (index === 0 && typeof onFirstMediaReady === 'function') {
                                            onFirstMediaReady(post_id, listIndex)
                                        }
                                    } catch {}
                                }}
                                onDoubleTap={Platform.OS !== 'web' && isPostPlayer ? doublePress : undefined}
                                onLongPress={Platform.OS !== 'web' && isPostPlayer ? doublePress : undefined}
                                onZoomChange={(isZooming) => {
                                    setIsImageZooming(isZooming)
                                    if (typeof onImageZoomChange === 'function') {
                                        onImageZoomChange(isZooming)
                                    }
                                }}
                            />
                            {/* Comentado temporalmente para permitir gestos de zoom
                        {Platform.OS !== 'web' && isPostPlayer && (
                            <GestureOverlayForImage
                                post_id={post_id}
                                onLongPressReaction={onLongPressReaction}
                                onDoubleTapReaction={onDoubleTapReaction}
                                DEBUG_GESTURES={DEBUG_GESTURES}
                            />
                        )}
                        */}
                        </View>
                    )

                case 'video':
                    return (
                        <View
                            style={[
                                // En web usamos width:'100%' para adaptarnos siempre al ancho de la columna,
                                // evitando depender de onLayout/containerWidth que puede no dispararse en CSS columns.
                                Platform.OS === 'web'
                                    ? { width: containerWidth || '100%', position: 'relative' }
                                    : {
                                          width: containerWidth,
                                          position: 'relative',
                                          // En mobile NO forzamos aspectRatio para evitar espacios blancos
                                          // El VideoPlayerAV determinará su altura basándose en el ratio real del video
                                          ...(function () {
                                              // Guardar ratio en caché si está disponible, pero no forzar layout
                                              if (ratioFromMeta && url) {
                                                  try {
                                                      SizeCache.setRatioForUrl(url, ratioFromMeta)
                                                  } catch {}
                                              }
                                              return {}
                                          })(),
                                      },
                                webItemRadius,
                            ]}
                        >
                            {/* En web ya no añadimos overlay negro externo; el póster del player maneja el placeholder */}
                            {/* En learning y posts (solo web), ofrecemos un botón de fullscreen propio SOLO para videos horizontales */}
                            {(() => {
                                const isHorizontalVideo = (() => {
                                    // obtener ratio desde metadata
                                    if (ratioFromMeta && ratioFromMeta >= 1) return true
                                    if (ratioFromMeta && ratioFromMeta < 1) return false

                                    // obtener desde caché
                                    const cachedRatio = url ? SizeCache.getRatioForUrl(url) : null
                                    if (cachedRatio && cachedRatio >= 1) return true
                                    if (cachedRatio && cachedRatio < 1) return false

                                    // Por defecto horizontal
                                    return true
                                })()

                                const shouldShowButton = Platform.OS === 'web' && (isLearning || isPosts) && isHorizontalVideo

                                // console.log('[MediaComponent] Fullscreen button render check:', {
                                //     shouldShowButton,
                                //     platform: Platform.OS,
                                //     isLearning,
                                //     isPosts,
                                //     path,
                                //     index,
                                //     currentIndex,
                                //     isHorizontalVideo,
                                //     ratioFromMeta,
                                // })

                                if (!shouldShowButton) return null

                                // En web usar un div nativo para asegurar que data-role se aplique correctamente
                                if (Platform.OS === 'web') {
                                    return (
                                        <div
                                            data-role='player-control'
                                            onClick={async (e) => {
                                                console.log('[MediaComponent] Fullscreen button clicked!', {
                                                    currentIndex,
                                                    path,
                                                    isLearning,
                                                    isPosts,
                                                    showFs,
                                                    mediasCount: medias?.length,
                                                    target: e.target,
                                                    dataRole: e.target.getAttribute?.('data-role'),
                                                })
                                                // Prevenir propagación del evento
                                                e.stopPropagation()
                                                e.preventDefault()

                                                try {
                                                    const ref = videoRefs.current[currentIndex]
                                                    console.log('[MediaComponent] Video ref:', ref ? 'exists' : 'null')
                                                    const status = await ref?.getStatusAsync?.()
                                                    console.log('[MediaComponent] Video status:', {
                                                        position: status?.positionMillis,
                                                        isPlaying: status?.isPlaying,
                                                    })
                                                    lastInlineStatusRef.current = {
                                                        position: Math.max(0, status?.positionMillis || 0),
                                                        isPlaying: !!status?.isPlaying,
                                                    }
                                                } catch (err) {
                                                    console.error('[MediaComponent] Error getting video status:', err)
                                                }
                                                console.log('[MediaComponent] Calling toogleFullScreen(true)')
                                                toogleFullScreen(true)
                                            }}
                                            style={{
                                                position: 'absolute',
                                                right: '16px',
                                                top: '16px',
                                                zIndex: 99999,
                                                backgroundColor: 'rgba(0,0,0,0.75)',
                                                padding: 10,
                                                borderRadius: '22px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                boxShadow: '0 3px 6px rgba(0,0,0,0.4)',
                                                pointerEvents: 'auto',
                                            }}
                                        >
                                            <Feather name='maximize' size={18} color='#fff' />
                                        </div>
                                    )
                                }
                                return null
                            })()}
                            {Platform.OS === 'web' ? (
                                // En web no envolver con Touchable para no interceptar clics de los controles (fullscreen, etc.)
                                <View style={{ width: containerWidth || '100%' }}>
                                    {/* Si el asset informa ratio, persistirlo antes de montar el player para alinear layout */}
                                    {(() => {
                                        try {
                                            if (url && ratioFromMeta && ratioFromMeta > 0.2 && ratioFromMeta < 5) {
                                                const persisted = SizeCache.getRatioForUrl(url)
                                                if (!(persisted && persisted > 0.2 && persisted < 5)) {
                                                    SizeCache.setRatioForUrl(url, ratioFromMeta)
                                                }
                                            }
                                        } catch {}
                                        return null
                                    })()}
                                    {/* DEBUG: Log para confirmar props en web */}
                                    {false &&
                                        isPostPlayer &&
                                        console.log('[MediaComponent] WEB Post player props:', {
                                            isPostPlayer,
                                            controlsMode: isPostPlayer ? 'post' : isLearning ? 'minimal' : 'native',
                                            autoplay,
                                            isVisible: isVisible && currentIndex === index,
                                        })}
                                    <VideoPlayerAV
                                        uri={url}
                                        onVideoRef={(ref) => {
                                            videoRefs.current[index] = ref
                                        }}
                                        autoplay={isLearning && countdownActive && index === currentIndex ? false : autoplay}
                                        isVisible={isVisible && currentIndex === index}
                                        post_id={`${post_id || path || 'learning'}:${index}:${(url || '').slice(-24)}`}
                                        isPodcast={false}
                                        contentSeen={setContentSeen}
                                        path={path}
                                        containerWidth={containerWidth}
                                        initialRatio={ratioFromMeta || undefined}
                                        prebufferGateMs={
                                            isPostPlayer && index === 0 && (listIndex === 0 || typeof listIndex === 'undefined') ? 3000 : 0
                                        }
                                        manageReplayOverlayExternally={!!isPostPlayer}
                                        onReady={() => {
                                            setReadyMap((m) => ({ ...m, [index]: true }))
                                            try {
                                                if (index === 0 && typeof onFirstMediaReady === 'function') {
                                                    onFirstMediaReady(post_id, listIndex)
                                                }
                                            } catch {}
                                        }}
                                        controlsMode={isPostPlayer ? 'post' : isLearning ? 'minimal' : 'native'}
                                        allowAutoplayAfterUserGesture={isLearning ? true : undefined}
                                        startMuted={isLearning ? true : undefined}
                                        externalShouldPlay={isLearning && index === currentIndex ? shouldPlayAfterCountdown : false}
                                        // En web para posts, montamos sin esperar naturalSize (el póster del player evita flash negro)
                                        waitForNaturalSize={isPostPlayer ? false : true}
                                        onPlaybackStatusUpdateExternal={(status) => {
                                            try {
                                                if (status?.isLoaded) {
                                                    setInlinePlayback((m) => ({
                                                        ...m,
                                                        [index]: {
                                                            position: Math.max(0, status.positionMillis || 0),
                                                            duration: Math.max(0, status.durationMillis || 0),
                                                            isPlaying: !!status.isPlaying,
                                                        },
                                                    }))
                                                    // Si se está reproduciendo, ocultar overlay de rewatch para este índice
                                                    if (status.isPlaying && endedMap[index]) {
                                                        setEndedMap((mm) => ({ ...mm, [index]: false }))
                                                    }
                                                }
                                                // Autoplay is now prevented via shouldPlay prop; no need to fight it here
                                                // Al finalizar el video, pasar al siguiente slide (sólo learnings)
                                                if (autoAdvanceOnVideoEnd && status?.didJustFinish) {
                                                    const hadFs = !!isFullscreenRef.current[index]
                                                    if (Platform.OS !== 'web' && hadFs) {
                                                        try {
                                                            videoRefs.current[index]?.dismissFullscreenPlayer?.()
                                                        } catch {}
                                                    }
                                                    const hasNext = index < (medias?.length || 0) - 1
                                                    if (hasNext) {
                                                        setTimeout(() => {
                                                            goToNext(false)
                                                            // Si veníamos de fullscreen, marcar re-entrada para el siguiente índice
                                                            if (Platform.OS !== 'web' && hadFs) {
                                                                pendingReenterFsIndex.current = index + 1
                                                            }
                                                        }, 120)
                                                    } else {
                                                        // Último: marcar visto para desbloquear siguiente módulo y completar el actual
                                                        try {
                                                            setContentSeen()
                                                        } catch {}
                                                        if (Platform.OS !== 'web' && hadFs) {
                                                            // Último: ya cerramos fullscreen arriba
                                                        }
                                                    }
                                                }
                                                // En posts: al finalizar mostrar botón "Volver a ver"
                                                if (!isLearning && isPostPlayer && status?.didJustFinish) {
                                                    setEndedMap((mm) => ({ ...mm, [index]: true }))
                                                }
                                                // Fallback near-end: si por algún motivo no llega didJustFinish
                                                if (
                                                    autoAdvanceOnVideoEnd &&
                                                    status?.isLoaded &&
                                                    typeof status?.durationMillis === 'number' &&
                                                    typeof status?.positionMillis === 'number'
                                                ) {
                                                    const remaining = status.durationMillis - status.positionMillis
                                                    const isLast = index >= (medias?.length || 0) - 1
                                                    // Si es el último slide, consideramos near-end a 5s para marcar visto (mejor UX)
                                                    if (
                                                        ((isLast && remaining <= 5000) || (!isLast && remaining <= 300)) &&
                                                        !endHandledRef.current[index]
                                                    ) {
                                                        endHandledRef.current[index] = true
                                                        const hadFs = !!isFullscreenRef.current[index]
                                                        if (Platform.OS !== 'web' && hadFs) {
                                                            try {
                                                                videoRefs.current[index]?.dismissFullscreenPlayer?.()
                                                            } catch {}
                                                        }
                                                        const hasNext = index < (medias?.length || 0) - 1
                                                        if (hasNext) {
                                                            setTimeout(() => {
                                                                goToNext(false)
                                                                if (Platform.OS !== 'web' && hadFs) {
                                                                    pendingReenterFsIndex.current = index + 1
                                                                }
                                                            }, 120)
                                                        } else {
                                                            // Último: marcar visto como fallback cerca del final
                                                            try {
                                                                setContentSeen()
                                                            } catch {}
                                                        }
                                                    }
                                                }
                                            } catch {}
                                        }}
                                        onFullscreenChange={(isFs) => {
                                            isFullscreenRef.current[index] = !!isFs
                                        }}
                                        onNaturalSize={(ns) => {
                                            if (naturalSizeCapturedRef.current[index]) return
                                            if (containerWidth && ns?.ratio && ns.ratio > 0.2 && ns.ratio < 5) {
                                                naturalSizeCapturedRef.current[index] = true
                                                const h = Math.round(containerWidth / ns.ratio)
                                                if (!heightMap[index]) setHeightMap((m) => ({ ...m, [index]: h }))
                                                const instanceId = `${post_id || path || 'learning'}:${index}:${(url || '').slice(-24)}`
                                                if (!SizeCache.getMediaHeight(instanceId)) SizeCache.setMediaHeight(instanceId, h)
                                                // Registrar altura máxima de media pura, NO sobreescribir altura total del post aquí
                                                try {
                                                    const postKey = post_id || `${path || 'learning'}:${index}`
                                                    if (!SizeCache.getPostMediaMaxHeight?.(postKey) || h > SizeCache.getPostMediaMaxHeight(postKey)) {
                                                        SizeCache.setPostMediaMaxHeight?.(postKey, h)
                                                    }
                                                } catch {}
                                                if (url) SizeCache.setRatioForUrl(url, ns.ratio)
                                            }
                                        }}
                                        key={`${post_id}-${index}`}
                                        isLearning={isLearning}
                                    />
                                </View>
                            ) : (
                                <View style={{ width: '100%' }}>
                                    {/* DEBUG: Log para confirmar props en mobile */}
                                    {false &&
                                        isPostPlayer &&
                                        console.log('[MediaComponent] MOBILE Post player props:', {
                                            isPostPlayer,
                                            controlsMode: isPostPlayer ? 'post' : isLearning ? 'minimal' : 'native',
                                            autoplay,
                                            isVisible: isVisible && currentIndex === index,
                                            Platform: Platform.OS,
                                        })}
                                    <VideoPlayerAV
                                        uri={url}
                                        onVideoRef={(ref) => {
                                            videoRefs.current[index] = ref
                                        }}
                                        autoplay={autoplay}
                                        isVisible={isVisible && currentIndex === index}
                                        post_id={`${post_id || path || 'learning'}:${index}:${(url || '').slice(-24)}`}
                                        isPodcast={false}
                                        contentSeen={setContentSeen}
                                        path={path}
                                        containerWidth={containerWidth}
                                        initialRatio={ratioFromMeta || undefined}
                                        prebufferGateMs={
                                            isPostPlayer && index === 0 && (listIndex === 0 || typeof listIndex === 'undefined') ? 3000 : 0
                                        }
                                        manageReplayOverlayExternally={!!isPostPlayer}
                                        onReady={() => {
                                            setReadyMap((m) => ({ ...m, [index]: true }))
                                            try {
                                                if (index === 0 && typeof onFirstMediaReady === 'function') {
                                                    onFirstMediaReady(post_id, listIndex)
                                                }
                                            } catch {}
                                        }}
                                        controlsMode={isPostPlayer ? 'post' : isLearning ? 'minimal' : 'native'}
                                        allowAutoplayAfterUserGesture={isLearning ? true : undefined}
                                        // startMuted={isLearning ? true : undefined}
                                        startMuted={false}
                                        externalShouldPlay={isLearning && index === currentIndex ? shouldPlayAfterCountdown : false}
                                        onLongPress={onLongPressReaction}
                                        onDoubleTap={doublePress}
                                        onVideoPress={() => {
                                            if (!isPostPlayer) return
                                            try {
                                                const navigationHook = require('../../navigation/hooks/RootNavigation')
                                                const { navigationRef } = navigationHook
                                                if (navigationRef.isReady()) {
                                                    navigationRef.navigate('ReelsViewer', { initialPostId: post_id })
                                                } else {
                                                    setTimeout(() => {
                                                        try {
                                                            navigationRef.navigate('ReelsViewer', { initialPostId: post_id })
                                                        } catch {}
                                                    }, 120)
                                                }
                                            } catch (e) {
                                                console.warn('open reels error', e)
                                            }
                                        }}
                                        onPlaybackStatusUpdateExternal={(status) => {
                                            try {
                                                if (status?.isLoaded) {
                                                    setInlinePlayback((m) => ({
                                                        ...m,
                                                        [index]: {
                                                            position: Math.max(0, status.positionMillis || 0),
                                                            duration: Math.max(0, status.durationMillis || 0),
                                                            isPlaying: !!status.isPlaying,
                                                        },
                                                    }))
                                                    // Si se está reproduciendo, ocultar overlay de rewatch para este índice
                                                    if (status.isPlaying && endedMap[index]) {
                                                        setEndedMap((mm) => ({ ...mm, [index]: false }))
                                                    }
                                                }
                                                if (countdownActive && status?.isPlaying) {
                                                    try {
                                                        if (!countdownCancelledRef.current[index]) {
                                                            countdownCancelledRef.current[index] = true
                                                            if (countdownIntervalRef.current) {
                                                                clearInterval(countdownIntervalRef.current)
                                                                countdownIntervalRef.current = null
                                                            }
                                                            setCountdownActive(false)
                                                            setCountdownRemaining(0)
                                                        }
                                                    } catch (e) {}
                                                }
                                                if (autoAdvanceOnVideoEnd && status?.didJustFinish) {
                                                    const hadFs = !!isFullscreenRef.current[index]
                                                    if (Platform.OS !== 'web' && hadFs) {
                                                        try {
                                                            videoRefs.current[index]?.dismissFullscreenPlayer?.()
                                                        } catch {}
                                                    }
                                                    const hasNext = index < (medias?.length || 0) - 1
                                                    if (hasNext) {
                                                        setTimeout(() => {
                                                            goToNext(false)
                                                            if (Platform.OS !== 'web' && hadFs) {
                                                                pendingReenterFsIndex.current = index + 1
                                                            }
                                                        }, 120)
                                                    } else {
                                                        // Último: marcar visto para desbloquear siguiente módulo
                                                        try {
                                                            setContentSeen()
                                                        } catch {}
                                                    }
                                                }
                                                // En posts: al finalizar mostrar botón "Volver a ver"
                                                if (!isLearning && isPostPlayer && status?.didJustFinish) {
                                                    setEndedMap((mm) => ({ ...mm, [index]: true }))
                                                }
                                                // comento porq manejo esto desde expo-av. esto me disparaba el setContentSeen apenas iniciaba el modulo con video
                                                // if (
                                                //     autoAdvanceOnVideoEnd &&
                                                //     status?.isLoaded &&
                                                //     typeof status?.durationMillis === 'number' &&
                                                //     typeof status?.positionMillis === 'number'
                                                // ) {
                                                //     const remaining = status.durationMillis - status.positionMillis
                                                //     const isLast = index >= (medias?.length || 0) - 1
                                                //     if (
                                                //         ((isLast && remaining <= 5000) || (!isLast && remaining <= 300)) &&
                                                //         !endHandledRef.current[index]
                                                //     ) {
                                                //         endHandledRef.current[index] = true
                                                //         const hadFs = !!isFullscreenRef.current[index]
                                                //         if (Platform.OS !== 'web' && hadFs) {
                                                //             try {
                                                //                 videoRefs.current[index]?.dismissFullscreenPlayer?.()
                                                //             } catch {}
                                                //         }
                                                //         const hasNext = index < (medias?.length || 0) - 1
                                                //         console.log('------------- Auto-advance near-end for index', index, 'hasNext:', hasNext)
                                                //         if (hasNext) {
                                                //             setTimeout(() => {
                                                //                 goToNext(false)
                                                //                 if (Platform.OS !== 'web' && hadFs) {
                                                //                     pendingReenterFsIndex.current = index + 1
                                                //                 }
                                                //             }, 120)
                                                //         } else {
                                                //             // Último: marcar visto como fallback
                                                //             try {
                                                //                 console.log('3 ------------------------- ejecuto este ---------- setContentSeen')
                                                //                 // TODO: este se me dispara apenas ingreso a un modulo con video, no espera a que llegue al final del video
                                                //                 setContentSeen()
                                                //             } catch {}
                                                //         }
                                                //     }
                                                // }
                                            } catch {}
                                        }}
                                        onFullscreenChange={(isFs) => {
                                            isFullscreenRef.current[index] = !!isFs
                                        }}
                                        onNaturalSize={(ns) => {
                                            if (naturalSizeCapturedRef.current[index]) return
                                            if (containerWidth && ns?.ratio && ns.ratio > 0.2 && ns.ratio < 5) {
                                                naturalSizeCapturedRef.current[index] = true
                                                const h = Math.round(containerWidth / ns.ratio)
                                                if (!heightMap[index]) setHeightMap((m) => ({ ...m, [index]: h }))
                                                const instanceId = `${post_id || path || 'learning'}:${index}:${(url || '').slice(-24)}`
                                                if (!SizeCache.getMediaHeight(instanceId)) SizeCache.setMediaHeight(instanceId, h)
                                                // Registrar altura máxima de media pura, NO sobreescribir altura total del post aquí
                                                try {
                                                    const postKey = post_id || `${path || 'learning'}:${index}`
                                                    if (!SizeCache.getPostMediaMaxHeight?.(postKey) || h > SizeCache.getPostMediaMaxHeight(postKey)) {
                                                        SizeCache.setPostMediaMaxHeight?.(postKey, h)
                                                    }
                                                } catch {}
                                                if (url) SizeCache.setRatioForUrl(url, ns.ratio)
                                            }
                                        }}
                                        key={`${post_id}-${index}`}
                                        waitForNaturalSize={true}
                                    />
                                </View>
                            )}
                            {/* Overlay de cuenta regresiva (solo learnings) */}
                            {isLearning &&
                                countdownActive &&
                                index === currentIndex &&
                                countdownRemaining != null &&
                                countdownRemaining > 0 &&
                                (() => {
                                    console.log('[MediaComponent][overlay] RENDERING with countdownRemaining:', countdownRemaining)
                                    return true
                                })() && (
                                    <TouchableOpacity
                                        key={`countdown-overlay-${index}-${countdownRemaining}`}
                                        activeOpacity={0.9}
                                        onPress={() => {
                                            // mark as cancelled so playback handler doesn't run repeatedly
                                            countdownCancelledRef.current[currentIndex] = true

                                            // clear any running countdown interval
                                            if (countdownIntervalRef.current) {
                                                clearInterval(countdownIntervalRef.current)
                                                countdownIntervalRef.current = null
                                            }

                                            setCountdownActive(false)
                                            setCountdownRemaining(0)
                                            setShouldPlayAfterCountdown(true)

                                            // Unmute and play after state updates
                                            setTimeout(() => {
                                                try {
                                                    videoRefs.current[currentIndex]?.setIsMutedAsync?.(false)
                                                    playVideo(currentIndex)
                                                } catch (e) {}
                                            }, 50)
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            backgroundColor: 'rgba(0,0,0,0.35)',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            borderRadius: 0,
                                        }}
                                    >
                                        <View style={{ alignItems: 'center' }}>
                                            <Text style={{ color: '#fff', fontSize: 14, marginBottom: 8 }}>El video se reproducirá en</Text>
                                            <View
                                                key={`countdown-wrapper-${countdownRemaining}-${Math.random()}`}
                                                style={{ width: 60, height: 60, justifyContent: 'center', alignItems: 'center' }}
                                            >
                                                {Platform.OS === 'web' ? (
                                                    <span
                                                        style={{
                                                            color: '#fff',
                                                            fontSize: '40px',
                                                            fontWeight: '800',
                                                            fontFamily: 'system-ui, -apple-system',
                                                            display: 'block',
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {countdownRemaining}
                                                    </span>
                                                ) : (
                                                    <Text style={{ color: '#fff', fontSize: 40, fontWeight: '800' }}>{countdownRemaining}</Text>
                                                )}
                                            </View>
                                            <Text style={{ color: '#fff', fontSize: 12, marginTop: 8 }}>Toca para reproducir ahora</Text>
                                        </View>
                                    </TouchableOpacity>
                                )}
                            {/* Overlay de rewatch (solo posts) */}
                            {!isLearning && isPostPlayer && endedMap[index] && index === currentIndex && (
                                <View
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0,0,0,0.35)',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        borderRadius: 0,
                                    }}
                                >
                                    <TouchableOpacity
                                        onPress={async () => {
                                            try {
                                                await videoRefs.current[index]?.setPositionAsync(0)
                                                await videoRefs.current[index]?.playAsync()
                                            } catch {}
                                            setEndedMap((mm) => ({ ...mm, [index]: false }))
                                        }}
                                        activeOpacity={0.9}
                                        style={{
                                            backgroundColor: '#fff',
                                            paddingHorizontal: 18,
                                            paddingVertical: 10,
                                            borderRadius: 22,
                                            shadowColor: '#000',
                                            shadowOpacity: 0.2,
                                            shadowRadius: 6,
                                            shadowOffset: { width: 0, height: 3 },
                                        }}
                                    >
                                        <Text style={{ color: '#111', fontSize: 14, fontWeight: '700' }}>Volver a ver</Text>
                                    </TouchableOpacity>
                                </View>
                            )}
                            {/* Inline custom controls for learning */}
                            {isLearning && false && (
                                <View
                                    style={{
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        paddingHorizontal: 12,
                                        paddingBottom: 10,
                                        paddingTop: 8,
                                        backgroundColor: 'rgba(0,0,0,0.28)',
                                    }}
                                >
                                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                        <TouchableOpacity
                                            onPress={async () => {
                                                try {
                                                    const st = inlinePlayback[index]
                                                    if (st?.isPlaying) await videoRefs.current[index]?.pauseAsync()
                                                    else await videoRefs.current[index]?.playAsync()
                                                } catch {}
                                            }}
                                            style={{ padding: 6, marginRight: 6 }}
                                            hitSlop={{ top: 6, left: 6, right: 6, bottom: 6 }}
                                        >
                                            {inlinePlayback[index]?.isPlaying ? (
                                                <Feather name='pause' size={16} color='#fff' />
                                            ) : (
                                                <Feather name='play' size={16} color='#fff' />
                                            )}
                                        </TouchableOpacity>
                                        {/* progress */}
                                        <View style={{ flex: 1, height: 22, justifyContent: 'center' }}>
                                            <View
                                                ref={(el) => {
                                                    inlineTrackRef.current[index] = el
                                                }}
                                                style={{ height: 4, backgroundColor: 'rgba(255,255,255,0.25)', borderRadius: 2 }}
                                            >
                                                <View
                                                    style={{
                                                        height: 4,
                                                        width: `${
                                                            inlinePlayback[index]?.duration > 0
                                                                ? (inlinePlayback[index]?.position / inlinePlayback[index]?.duration) * 100
                                                                : 0
                                                        }%`,
                                                        backgroundColor: '#fff',
                                                        borderRadius: 2,
                                                    }}
                                                />
                                            </View>
                                            <View
                                                onLayout={(e) => {
                                                    inlineTrackWidthRef.current[index] = e?.nativeEvent?.layout?.width || 0
                                                }}
                                                style={{ ...StyleSheet.absoluteFillObject }}
                                            >
                                                <TouchableOpacity
                                                    onPress={async (e) => {
                                                        try {
                                                            const rect = inlineTrackRef.current[index]?.getBoundingClientRect?.()
                                                            const pageX = e?.nativeEvent?.pageX ?? e?.nativeEvent?.clientX
                                                            const w = rect?.width || inlineTrackWidthRef.current[index] || 0
                                                            const x =
                                                                typeof pageX === 'number' && rect ? pageX - rect.left : e?.nativeEvent?.locationX || 0
                                                            const dur = inlinePlayback[index]?.duration || 0
                                                            if (w > 0 && dur > 0) {
                                                                const pct = Math.max(0, Math.min(1, x / w))
                                                                const newPos = Math.round(dur * pct)
                                                                const wasPlaying = !!inlinePlayback[index]?.isPlaying
                                                                await videoRefs.current[index]?.setPositionAsync(newPos)
                                                                if (wasPlaying) await videoRefs.current[index]?.playAsync()
                                                            }
                                                        } catch {}
                                                    }}
                                                    activeOpacity={1}
                                                    style={{ width: '100%', height: '100%' }}
                                                    onStartShouldSetResponder={() => Platform.OS === 'web'}
                                                    onResponderGrant={async (ev) => {
                                                        if (Platform.OS !== 'web') return
                                                        try {
                                                            const rect = inlineTrackRef.current[index]?.getBoundingClientRect?.()
                                                            const pageX = ev?.nativeEvent?.pageX ?? ev?.nativeEvent?.clientX
                                                            const w = rect?.width || inlineTrackWidthRef.current[index] || 0
                                                            const dur = inlinePlayback[index]?.duration || 0
                                                            if (!w || !dur || typeof pageX !== 'number') return
                                                            const pct = Math.max(0, Math.min(1, (pageX - rect.left) / w))
                                                            const newPos = Math.round(dur * pct)
                                                            const st = inlinePlayback[index]
                                                            const wasPlaying = !!st?.isPlaying
                                                            if (wasPlaying) await videoRefs.current[index]?.pauseAsync()
                                                            await videoRefs.current[index]?.setPositionAsync(newPos)
                                                            if (wasPlaying) await videoRefs.current[index]?.playAsync()
                                                        } catch {}
                                                    }}
                                                    onResponderMove={async (ev) => {
                                                        if (Platform.OS !== 'web') return
                                                        try {
                                                            const rect = inlineTrackRef.current[index]?.getBoundingClientRect?.()
                                                            const pageX = ev?.nativeEvent?.pageX ?? ev?.nativeEvent?.clientX
                                                            const w = rect?.width || inlineTrackWidthRef.current[index] || 0
                                                            const dur = inlinePlayback[index]?.duration || 0
                                                            if (!w || !dur || typeof pageX !== 'number') return
                                                            const pct = Math.max(0, Math.min(1, (pageX - rect.left) / w))
                                                            const newPos = Math.round(dur * pct)
                                                            await videoRefs.current[index]?.setPositionAsync(newPos)
                                                        } catch {}
                                                    }}
                                                    onResponderRelease={() => {
                                                        /* noop, ya reanudamos si hacía falta */
                                                    }}
                                                />
                                            </View>
                                        </View>
                                        <Text style={{ color: '#fff', fontSize: 10, marginLeft: 6 }}>
                                            {(() => {
                                                const pos = Math.max(0, inlinePlayback[index]?.position || 0)
                                                const dur = Math.max(0, inlinePlayback[index]?.duration || 0)
                                                const toTime = (ms) => {
                                                    const s = Math.floor((ms || 0) / 1000)
                                                    const m = Math.floor(s / 60)
                                                    const ss = s % 60
                                                    return `${m}:${ss < 10 ? '0' : ''}${ss}`
                                                }
                                                return `${toTime(pos)} / ${toTime(dur)}`
                                            })()}
                                        </Text>
                                    </View>
                                </View>
                            )}
                        </View>
                    )
                case 'pdf':
                    //@FixMe: validates OS bc webview is not supported in web
                    return (
                        <View style={[{ width: containerWidth }, Platform.OS == 'web' && webItemRadius]}>
                            {!isWeb() ? (
                                <WebViewButton
                                    url={webViewUrl}
                                    preview={item.media_preview}
                                    style={styles.pdf}
                                    contentSeen={setContentSeen}
                                    path={path}
                                />
                            ) : (
                                <WebRedirect downloadUrl={url} containerWidth={containerWidth} contentSeen={setContentSeen} />
                            )}
                        </View>
                    )
                case 'survey':
                    return item.survey ? (
                        <View style={[{ width: containerWidth }, Platform.OS == 'web' && webItemRadius]}>
                            <PostSurvey
                                Survey={item.survey}
                                handleComplete={() => {
                                    handleCompleteSurvey()
                                    setContentSeen()
                                }}
                                post_id={post_id}
                            />
                        </View>
                    ) : (
                        <Text>No se pudo cargar la encuesta</Text>
                    )
                case 'podcast':
                    return (
                        <View style={[{ width: containerWidth }, Platform.OS == 'web' && webItemRadius]}>
                            <AudioPlayerWaveform
                                url={url}
                                title={item.title || 'PODCAST'}
                                post_id={post_id}
                                nearEndThresholdSec={index >= (medias?.length || 0) - 1 ? 5 : 0.3}
                                onEnd={() => {
                                    try {
                                        const hasNext = index < (medias?.length || 0) - 1
                                        if (autoAdvanceOnVideoEnd && hasNext) {
                                            // Mantener consistencia con videos: avanzar automáticamente en learnings
                                            goToNext(false)
                                        } else {
                                            // Marcar visto si es el último; habilita NextModule/evaluación en CourseHome
                                            setContentSeen()
                                        }
                                    } catch {}
                                }}
                            />
                        </View>
                    )
                default:
                    return null //<Text>Hubo un error al cargar la media</Text>
            }
        },
        [
            containerWidth,
            currentIndex,
            autoplay,
            isVisible,
            post_id,
            path,
            isPostPlayer,
            onFirstMediaReady,
            listIndex,
            onDoubleTapReaction,
            onLongPressReaction,
            onImageZoomChange,
            handleCompleteSurvey,
            autoAdvanceOnVideoEnd,
            medias,
            countdownRemaining,
            countdownActive,
            shouldPlayAfterCountdown,
            inlinePlayback,
        ],
    )

    const setContentSeen = () => {
        if (currentIndex === medias.length - 1) {
            try {
                if (typeof onPageChange === 'function') onPageChange(true)
            } catch {}
            try {
                contentSeen && contentSeen()
            } catch {}
            try {
                if (isUltimateModule && typeof onLastContentSeen === 'function') {
                    onLastContentSeen()
                }
            } catch {}
        }
    }

    // Efecto para actualizar la animación cuando cambia currentIndex
    useEffect(() => {
        // debug suprimido: animation currentIndex
        Animated.timing(animation, {
            toValue: currentIndex,
            duration: 300, // Duración de la animación
            easing: Easing.ease, // Easing para suavizar la animación
            useNativeDriver: false, // No usamos Native Driver porque animamos el estilo en el contenedor
        }).start()
    }, [currentIndex])

    // Configuración de bullets: dots uniformes + "píldora" activa animada con auto-scroll
    const DOT_W = 6
    const DOT_SPACING = 4 // marginHorizontal total (2 a cada lado)
    const STEP = DOT_W + DOT_SPACING // distancia entre centros/apariciones para dots normales
    const ACTIVE_W = 25 // coincide con styles.currentBulletIndicator.width
    const SIDE_PADDING = 10 // coincide con styles.bulletsIndicator.paddingHorizontal
    const [bulletsContainerW, setBulletsContainerW] = useState(0)
    const bulletsScrollRef = useRef(null)

    // Auto-scroll para centrar el bullet activo cuando hay muchos
    useEffect(() => {
        if (!bulletsScrollRef.current || medias.length <= 1 || bulletsContainerW <= 0) return
        // Ancho total aproximado considerando un activo más ancho
        const contentWidth = (medias.length - 1) * STEP + ACTIVE_W + DOT_SPACING + SIDE_PADDING * 2
        // Si el contenido cabe, no auto-scroll; centramos con padding dinámico
        if (contentWidth <= bulletsContainerW) return
        const target = Math.max(
            0,
            Math.min(
                Math.max(0, contentWidth - bulletsContainerW),
                // Desplazar hacia el centro del activo: usar ancho activo cuando corresponde
                currentIndex * STEP - bulletsContainerW / 2 + SIDE_PADDING + (currentIndex > 0 ? ACTIVE_W / 2 : DOT_W / 2),
            ),
        )
        try {
            bulletsScrollRef.current.scrollTo({ x: target, animated: true })
        } catch {}
    }, [currentIndex, bulletsContainerW, medias.length])

    // Evitar micro-saltos por cambios mínimos de ancho del contenedor
    const lastWidthRef = useRef(containerWidth)
    const handleLayout = (e) => {
        const w = e.nativeEvent.layout.width
        if (typeof w !== 'number' || w <= 0) return
        // Umbral de 2px para evitar updates por subpíxeles / rounding
        if (!lastWidthRef.current || Math.abs(lastWidthRef.current - w) >= 2) {
            lastWidthRef.current = w
            setContainerWidth(w)
        }
    }

    // Web: usar ResizeObserver para medir el ancho del contenedor dentro de CSS Columns
    useEffect(() => {
        if (Platform.OS !== 'web') return
        let ro
        const el = rootRef.current
        try {
            if (el && el.getBoundingClientRect) {
                const rect = el.getBoundingClientRect()
                if (rect && rect.width > 0) {
                    if (!lastWidthRef.current || Math.abs(lastWidthRef.current - rect.width) >= 2) {
                        lastWidthRef.current = rect.width
                        setContainerWidth(Math.round(rect.width))
                    }
                }
            }
            if (typeof ResizeObserver !== 'undefined' && el) {
                ro = new ResizeObserver((entries) => {
                    try {
                        const entry = entries && entries[0]
                        const w = entry?.contentRect?.width
                        if (typeof w === 'number' && w > 0) {
                            if (!lastWidthRef.current || Math.abs(lastWidthRef.current - w) >= 2) {
                                lastWidthRef.current = w
                                setContainerWidth(Math.round(w))
                            }
                        }
                    } catch {}
                })
                ro.observe(el)
            } else if (el && el.getBoundingClientRect) {
                const onWinResize = () => {
                    try {
                        const r = el.getBoundingClientRect()
                        const w = r?.width || 0
                        if (w > 0 && (!lastWidthRef.current || Math.abs(lastWidthRef.current - w) >= 2)) {
                            lastWidthRef.current = w
                            setContainerWidth(Math.round(w))
                        }
                    } catch {}
                }
                window.addEventListener('resize', onWinResize, { passive: true })
                ro = { disconnect: () => window.removeEventListener('resize', onWinResize) }
            }
        } catch {}
        return () => {
            try {
                ro && ro.disconnect && ro.disconnect()
            } catch {}
        }
    }, [rootRef.current])
    async function toogleFullScreen(visible) {
        if (Platform.OS === 'android') {
            if (visible) {
                NavigationBar.setBehaviorAsync('sticky-immersive')
                NavigationBar.setVisibilityAsync('hidden')
            } else {
                NavigationBar.setVisibilityAsync('visible')
                NavigationBar.setBehaviorAsync('inset-touch')
            }
        }
        setShowFs(visible)
    }
    return (
        <View
            ref={rootRef}
            onLayout={handleLayout}
            style={{
                width: '100%',
                minHeight: containerWidth > 0 ? undefined : 180,
                overflow: Platform.OS === 'web' ? 'hidden' : 'visible',
                zIndex: isImageZooming ? 99999 : 1,
                elevation: isImageZooming ? 999 : 0,
            }}
        >
            {containerWidth > 0 ? (
                <>
                    <Animated.FlatList
                        ref={flatListRef}
                        horizontal={true}
                        showsHorizontalScrollIndicator={false}
                        pagingEnabled
                        onScroll={onScroll}
                        style={{ overflow: 'visible' }}
                        contentContainerStyle={{ overflow: 'visible' }}
                        onScrollBeginDrag={() => {
                            // Marca navegación manual por swipe
                            isManualNavigation.current = true
                            isUserDraggingRef.current = true
                        }}
                        onMomentumScrollEnd={(e) => {
                            try {
                                const x = e?.nativeEvent?.contentOffset?.x || 0
                                const width = containerWidth || defaultWidth || 1
                                const newIndex = Math.max(0, Math.min(medias.length - 1, Math.round(x / width)))
                                setCurrentIndex(newIndex)
                                onPageChange(newIndex === medias.length - 1)

                                const currentMedia = medias?.[newIndex]
                                const mediaType = (currentMedia?.media_type || currentMedia?.content_type || '').toString().toLowerCase()
                                if (['image'].includes(mediaType)) {
                                    // solo para type image, agregar mas types de ser necesario
                                    setContentSeen()
                                }
                            } catch {}
                            // Restaurar flags inmediatamente al terminar la inercia
                            isManualNavigation.current = false
                            isUserDraggingRef.current = false
                        }}
                        data={medias}
                        keyExtractor={keyExtractor}
                        renderItem={renderItem}
                        getItemLayout={getItemLayout}
                        viewabilityConfig={viewabilityConfig.current}
                        onViewableItemsChanged={onViewableItemsChanged}
                        // onViewableItemsChanged={handleViewableItemsChanged.current}
                        onEndReached={onEndReached} // Llama a onEndReached cuando se llega al final de la lista
                        // removeClippedSubviews={true} // Elimina los elementos que no están en la vista
                    />

                    {/* Flechas de navegación - Solo en web cuando hay múltiples medias */}
                    {isWeb() && medias.length > 1 && (
                        <>
                            {/* Flecha izquierda */}
                            {currentIndex > 0 && (
                                <Pressable
                                    id='media-nav-btn-left'
                                    onPress={(e) => {
                                        if (e && e.preventDefault) e.preventDefault()
                                        if (e && e.stopPropagation) e.stopPropagation()
                                        goToPrevious()
                                    }}
                                    onStartShouldSetResponder={() => true}
                                    style={{
                                        position: 'absolute',
                                        left: 16,
                                        top: '50%',
                                        transform: [{ translateY: -20 }],
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        width: 40,
                                        height: 40,
                                        borderRadius: 20,
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        zIndex: 30,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (isWeb) e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'
                                    }}
                                    onMouseLeave={(e) => {
                                        if (isWeb) e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
                                    }}
                                >
                                    <Ionicons name='chevron-back' size={24} color='white' />
                                </Pressable>
                            )}

                            {/* Flecha derecha */}
                            {currentIndex < medias.length - 1 && (
                                <Pressable
                                    id='media-nav-btn-right'
                                    onPress={(e) => {
                                        if (e && e.preventDefault) e.preventDefault()
                                        if (e && e.stopPropagation) e.stopPropagation()
                                        goToNext()
                                    }}
                                    onStartShouldSetResponder={() => true}
                                    style={{
                                        position: 'absolute',
                                        right: 16,
                                        top: '50%',
                                        transform: [{ translateY: -20 }],
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        width: 40,
                                        height: 40,
                                        borderRadius: 20,
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        zIndex: 30,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (isWeb) e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'
                                    }}
                                    onMouseLeave={(e) => {
                                        if (isWeb) e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)'
                                    }}
                                >
                                    <Ionicons name='chevron-forward' size={24} color='white' />
                                </Pressable>
                            )}
                        </>
                    )}
                </>
            ) : (
                // Placeholder mientras medimos el ancho real del contenedor (evita saltos y overflow)
                <View style={{ width: '100%', aspectRatio: 16 / 9, backgroundColor: '#E9ECEF', borderRadius: 0 }} />
            )}

            {/* Fullscreen propio para learning y posts (solo web) */}
            {Platform.OS === 'web' && (isLearning || isPosts) && (
                <LearningFullscreen
                    visible={showFs}
                    medias={medias}
                    startIndex={currentIndex}
                    onClose={async (payload) => {
                        toogleFullScreen(false)
                        try {
                            // payload: { index, position, isPlaying }
                            const idx = typeof payload?.index === 'number' ? payload.index : currentIndex
                            if (idx !== currentIndex && flatListRef.current) {
                                // mantener continuidad visual si el usuario cambió de slide en fullscreen
                                setCurrentIndex(idx)
                                flatListRef.current.scrollToIndex({ index: idx, animated: false })
                            }
                            const ref = videoRefs.current[idx]
                            if (ref && typeof payload?.position === 'number') {
                                await ref.setPositionAsync(Math.max(0, payload.position))
                                if (payload?.isPlaying) await ref.playAsync()
                                else await ref.pauseAsync()
                                // Si cerró el fullscreen casi al final del último slide, marcar visto para completar módulo
                                try {
                                    const st = await ref.getStatusAsync()
                                    const dur = Math.max(0, st?.durationMillis || 0)
                                    const pos = Math.max(0, st?.positionMillis || 0)
                                    const isLast = idx >= (medias?.length || 0) - 1
                                    if (isLast && dur > 0 && dur - pos <= 5000) {
                                        setContentSeen()
                                    }
                                } catch {}
                            }
                        } catch {}
                    }}
                    autoplay={autoplay}
                    countdownSeconds={countdownSeconds}
                    path={path}
                    resumeKey={resumeKey}
                    initialPosition={lastInlineStatusRef.current?.position}
                    initialIsPlaying={lastInlineStatusRef.current?.isPlaying}
                    onClickPlayPause={() => {}}
                    onClickPosition={(v) => {}}
                />
            )}

            {/* Contador flotante de cursos */}
            {!isPost && containerWidth > 0 && medias.length > 1 && !isImageZooming ? (
                <View style={styles.floatPageIndicator}>
                    <Text style={styles.floatPageIndicatorText}>
                        {currentIndex + 1}/{medias.length}
                    </Text>
                </View>
            ) : null}
            {containerWidth > 0 && medias.length > 1 && !isImageZooming ? (
                <View style={{ zIndex: 10 }}>
                    {(() => {
                        const bulletsPos = isGoTo
                            ? {
                                  position: 'absolute',
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  zIndex: 1000,
                                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                              }
                            : { zIndex: 10, bottom: 0 }
                        const contentWidth = (medias.length - 1) * STEP + ACTIVE_W + DOT_SPACING + SIDE_PADDING * 2
                        const dotsRow = (
                            <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: SIDE_PADDING }}>
                                {medias.map((_, i) => {
                                    const isActive = i === currentIndex
                                    return (
                                        <Animated.View
                                            key={`bullet-${i}`}
                                            style={[
                                                isActive ? styles.currentBulletIndicator : styles.circleBulletIndicator,
                                                isActive
                                                    ? {
                                                          transform: [
                                                              {
                                                                  scale: animation.interpolate({
                                                                      inputRange: [i - 1, i, i + 1],
                                                                      outputRange: [0.95, 1, 0.95],
                                                                      extrapolate: 'clamp',
                                                                  }),
                                                              },
                                                          ],
                                                      }
                                                    : null,
                                            ]}
                                        />
                                    )
                                })}
                            </View>
                        )
                        return (
                            <View
                                style={[styles.bulletsContainer, { height: isGoTo ? 0 : 18, zIndex: 10 }]}
                                onLayout={(e) => setBulletsContainerW(e?.nativeEvent?.layout?.width || 0)}
                            >
                                {contentWidth <= bulletsContainerW ? (
                                    <View style={[styles.bulletsIndicator, bulletsPos]}>{dotsRow}</View>
                                ) : (
                                    <ScrollView
                                        ref={bulletsScrollRef}
                                        horizontal
                                        showsHorizontalScrollIndicator={false}
                                        style={bulletsPos}
                                        contentContainerStyle={[styles.bulletsIndicator, { position: 'relative', bottom: 0, zIndex: 10 }]}
                                    >
                                        {dotsRow}
                                    </ScrollView>
                                )}
                            </View>
                        )
                    })()}
                </View>
            ) : null}

            {medias[currentIndex] && medias[currentIndex].description_long && (
                <View style={styles.containerDescription}>
                    <Text style={styles.descriptionModule}>{medias[currentIndex].description_long}</Text>
                </View>
            )}
        </View>
    )
}

export default MediaComponent
